import {
  Box,
  Icons,
  Pie,
  PieChartContainer,
  Skeleton,
  Stack,
  tokens,
  Tooltip,
  Typography,
} from '@cast/design-system';
import { FailedToLoad } from '@cast/shared-messages-ui';
import { dashesOrNum, formatPercentage } from '@cast/utils';

import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { CardCurrentDate } from '../_components/CardCurrentDate';
import { InventoryListItem } from '../../../_components/ui/InventoryListItem/InventoryListItem';
import { useInventoryWorkloadsDashboardNavigation } from '../_hooks/useInventoryWorkloadsDashboardNavigation';
import { COVERAGE_DIMENSION_FILTERS } from '../_utils/dashboardListNavigation';
import { useWorkloadAutoscalerCoverageData } from './_hooks/useWorkloadAutoscalerCoverageData';

const CARD_TITLE = 'Workload Autoscaler coverage';

const SKELETON_DIMENSION_KEYS = ['vertical', 'horizontal', 'both', 'disabled'];

const WorkloadAutoscalerCoverageCardSkeleton = () => (
  <Stack height="100%" gap={24} justifyContent="space-between">
    <Stack direction="row" gap={12} alignItems="center" sx={{ flexShrink: 0 }}>
      <Skeleton variant="circular" width={80} height={80} />
      <Stack gap={2}>
        <Skeleton variant="text" width={80} />
        <Skeleton variant="text" width={160} />
      </Stack>
    </Stack>

    <Stack gap={0}>
      <Skeleton variant="text" width={120} sx={{ pb: 8 }} />
      {SKELETON_DIMENSION_KEYS.map((key, index) => (
        <InventoryListItem
          key={key}
          size="sm"
          divider={index < SKELETON_DIMENSION_KEYS.length - 1}
          slots={{
            startContent: (
              <Skeleton
                variant="rectangular"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: tokens.theme.radius.xs,
                }}
              />
            ),
            label: <Skeleton variant="text" width={80} />,
            endContent: <Skeleton variant="text" width={24} />,
          }}
        />
      ))}
    </Stack>
  </Stack>
);

export const WorkloadAutoscalerCoverageCard = () => {
  const { data, isLoading, isFetching, hasError, refetch } =
    useWorkloadAutoscalerCoverageData();
  const { navigateToAutoscalingList } =
    useInventoryWorkloadsDashboardNavigation();

  const isCardLoading = isLoading || (hasError && isFetching);

  const coverageData = [
    {
      key: 'vertical',
      label: 'Vertical',
      value: data?.verticalCount ?? 0,
      fill: tokens.theme.data.grey900,
    },
    {
      key: 'horizontal',
      label: 'Horizontal',
      value: data?.horizontalCount ?? 0,
      fill: tokens.theme.data.grey600,
    },
    {
      key: 'both',
      label: 'Both dimensions',
      value: data?.bothCount ?? 0,
      fill: tokens.theme.data.grey400,
    },
    {
      key: 'disabled',
      label: 'Disabled',
      value: data?.disabledCount ?? 0,
      fill: tokens.theme.data.grey200,
    },
  ];

  const pieData = coverageData.map((dimension) => ({
    value: dimension.value,
    fill: dimension.fill,
  }));

  return (
    <WidgetLayout
      title={CARD_TITLE}
      testId="workload-autoscaler-coverage-card"
      headerEndContent={<CardCurrentDate />}
    >
      {isCardLoading ? (
        <WorkloadAutoscalerCoverageCardSkeleton />
      ) : hasError ? (
        <FailedToLoad
          title="Failed to load Workload Autoscaler coverage"
          refresh={refetch}
        />
      ) : (
        <Stack height="100%" gap={24} justifyContent="space-between">
          <Stack
            direction="row"
            gap={12}
            alignItems="center"
            data-testid="workload-autoscaler-coverage-card-summary"
            sx={{ flexShrink: 0 }}
          >
            <Box sx={{ width: 41, height: 80 }}>
              <PieChartContainer
                testId="workload-autoscaler-coverage-pie"
                variant="half-right"
                data={pieData}
                radius={40}
              >
                <Pie dataKey="value" />
              </PieChartContainer>
            </Box>

            <Stack gap={2}>
              <Typography variant="metricmd" data-testid="coverage-percentage">
                {formatPercentage(data?.percentage, { roundBy: 1 })}
              </Typography>
              <Stack direction="row" gap={4} alignItems="center">
                <Typography variant="P12M" color="textSecondary">
                  {`${dashesOrNum({
                    value: data?.cpuCoresDifference,
                    roundBy: 2,
                  })} CPU across ${data?.totalCount ?? '—'} workloads`}
                </Typography>
                <Tooltip
                  title="Workloads the Autoscaler upscaled to meet performance needs."
                  placement="top"
                >
                  <Icons.Info size={14} />
                </Tooltip>
              </Stack>
            </Stack>
          </Stack>

          <Stack
            gap={0}
            flex="0 1 auto"
            minHeight={0}
            overflow="auto"
            data-testid="workload-autoscaler-coverage-card-details"
          >
            <Typography
              variant="L10M"
              color="textSecondary"
              sx={{ textTransform: 'uppercase', pb: 8 }}
            >
              Workloads autoscaled by dimension
            </Typography>
            {coverageData.map((item, index) => (
              <InventoryListItem
                key={item.key}
                size="sm"
                divider={index < coverageData.length - 1}
                onClick={() =>
                  navigateToAutoscalingList(
                    COVERAGE_DIMENSION_FILTERS[item.key]
                  )
                }
                slots={{
                  startContent: (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: tokens.theme.radius.xs,
                        backgroundColor: item.fill,
                      }}
                    />
                  ),
                  label: item.label,
                  endContent: item.value,
                }}
                slotProps={{
                  endContent: { variant: 'P12B' },
                }}
              />
            ))}
          </Stack>
        </Stack>
      )}
    </WidgetLayout>
  );
};
